import React from 'react';
import { motion } from 'framer-motion';
import { FiCode, FiServer, FiZap, FiSmartphone } from 'react-icons/fi';
import { ResumeData } from '../types/resume.types';

interface AboutProps {
  data: ResumeData;
}

const specialties = [
  {
    icon: FiCode,
    title: 'Web Testing',
    description: 'Selenium & Robot Framework automation for web applications'
  },
  {
    icon: FiServer,
    title: 'API Testing',
    description: 'RESTful API testing with Postman and Python automation'
  },
  {
    icon: FiZap,
    title: 'Performance Testing',
    description: 'Load testing and rate-limit validation with JMeter'
  },
  {
    icon: FiSmartphone,
    title: 'Mobile Testing',
    description: 'Cross-platform mobile application testing'
  }
];

const About: React.FC<AboutProps> = ({ data }) => {
  return (
    <section id="about" className="pt-24 pb-16 section-padding gradient-bg">
      <div className="container-width">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            About Me
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl card-shadow">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                My Journey
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
                {data.about}
              </p>
              
              <div className="mt-8 flex flex-wrap gap-3">
                <div className="flex items-center space-x-2 bg-primary-50 dark:bg-primary-900/20 px-4 py-2 rounded-full">
                  <div className="w-3 h-3 bg-accent-500 rounded-full animate-pulse"></div>
                  <span className="text-primary-700 dark:text-primary-300 font-medium">Available for opportunities</span>
                </div>
                <div className="flex items-center space-x-2 bg-secondary-50 dark:bg-secondary-900/20 px-4 py-2 rounded-full">
                  <span className="text-secondary-700 dark:text-secondary-300 font-medium">5+ Years Experience</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {specialties.map((specialty, index) => {
              const Icon = specialty.icon;
              return (
                <motion.div
                  key={specialty.title}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-white dark:bg-gray-800 p-6 rounded-xl card-shadow hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {specialty.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {specialty.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;